import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";
import { Button } from "./ui/button";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/events", label: "Events" },
    { path: "/faq", label: "FAQ" },
    { path: "/signup", label: "Community" }
  ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <nav className="flex items-center p-6 relative z-20 bg-white shadow-sm border-b border-gray-100">
      <Link to="/" className="flex items-center space-x-4 group">
        <div className="transition-transform duration-300 group-hover:scale-105">
          <img 
            src="https://customer-assets.emergentagent.com/job_centered-nav/artifacts/o3oyffod_0AB76696-D43D-4560-996B-0C817906B442.PNG" 
            alt="takeatrip NYC" 
            className="h-16 w-auto"
          />
        </div>
      </Link>
      
      {/* Desktop Navigation - Perfectly Centered */}
      <div className="hidden md:flex items-center space-x-8 absolute left-1/2 transform -translate-x-1/2">
        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className={`text-lg font-medium transition-all duration-300 ${
              location.pathname === link.path 
                ? "border-b-2 pb-1" 
                : "text-slate-700"
            }`}
            style={{
              color: location.pathname === link.path ? '#1d476a' : '#334155',
              borderColor: location.pathname === link.path ? '#1d476a' : 'transparent'
            }}
            onMouseEnter={(e) => {
              if (location.pathname !== link.path) {
                e.target.style.color = '#1d476a';
              }
            }}
            onMouseLeave={(e) => {
              if (location.pathname !== link.path) {
                e.target.style.color = '#334155';
              }
            }}
          >
            {link.label}
          </Link>
        ))}
      </div>

      <div className="flex items-center space-x-4 ml-auto">
        <Link to="/slope-social-i">
          <Button 
            className="hidden sm:flex transition-all duration-300 mr-2 px-6 py-3 text-lg font-semibold"
            style={{
              background: '#7FC8E8',
              color: 'white',
              border: 'none'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#5CB3D9';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#7FC8E8';
              e.target.style.transform = 'scale(1)';
            }}
          >
            Slope Social I
          </Button>
        </Link>

        {/* Social Media Icons */}
        <div className="hidden sm:flex items-center space-x-3">
          {/* Instagram Icon */}
          <button
            onClick={() => window.open('https://www.instagram.com/takeatripnyc/', '_blank')}
            className="p-2 rounded-full transition-all duration-300 hover:bg-gray-100"
            style={{
              color: '#1d476a'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(29, 71, 106, 0.1)';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <Instagram className="h-5 w-5" />
          </button>

          {/* TikTok Icon */}
          <button
            onClick={() => window.open('https://www.tiktok.com/@takeatrip_nyc?_t=ZT-900gxeZ0HvC&_r=1', '_blank')}
            className="p-2 rounded-full transition-all duration-300 hover:bg-gray-100"
            style={{
              color: '#1d476a'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(29, 71, 106, 0.1)';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43V7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.43z"/>
            </svg>
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMenu}
          className="md:hidden p-2 text-slate-700 transition-colors duration-300"
          style={{
            color: isMenuOpen ? '#1d476a' : '#334155'
          }}
          onMouseEnter={(e) => e.target.style.color = '#1d476a'}
          onMouseLeave={(e) => e.target.style.color = isMenuOpen ? '#1d476a' : '#334155'}
        >
          {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <>
          {/* Backdrop to block hero text */}
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm md:hidden z-40" onClick={toggleMenu}></div>
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-b border-gray-100 md:hidden z-50" style={{backgroundColor: 'rgba(255, 255, 255, 0.98)', backdropFilter: 'blur(10px)'}}>
            <div className="flex flex-col space-y-4 p-6 text-left">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={toggleMenu}
                className={`text-lg font-medium transition-colors duration-300 ${
                  location.pathname === link.path 
                    ? "" 
                    : "text-slate-700"
                }`}
                style={{
                  color: location.pathname === link.path ? '#1d476a' : '#334155'
                }}
                onMouseEnter={(e) => e.target.style.color = '#1d476a'}
                onMouseLeave={(e) => {
                  if (location.pathname !== link.path) {
                    e.target.style.color = '#334155';
                  }
                }}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/slope-social-i">
              <Button 
                className="transition-all duration-300 w-full mb-4 py-3 text-lg font-semibold"
                style={{
                  background: '#7FC8E8',
                  color: 'white',
                  border: 'none'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#5CB3D9';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#7FC8E8';
                }}
                onClick={toggleMenu}
              >
                Slope Social I
              </Button>
            </Link>

            {/* Mobile Social Media Icons */}
            <div className="flex items-center justify-center space-x-4 pt-2 border-t border-gray-200">
              {/* Instagram Icon */}
              <button
                onClick={() => {
                  window.open('https://www.instagram.com/takeatripnyc/', '_blank');
                  toggleMenu();
                }}
                className="p-3 rounded-full transition-all duration-300"
                style={{
                  color: '#1d476a',
                  backgroundColor: 'rgba(29, 71, 106, 0.05)'
                }}
              >
                <Instagram className="h-6 w-6" />
              </button>

              {/* TikTok Icon */}
              <button
                onClick={() => {
                  window.open('https://www.tiktok.com/@takeatrip_nyc?_t=ZT-900gxeZ0HvC&_r=1', '_blank');
                  toggleMenu();
                }}
                className="p-3 rounded-full transition-all duration-300"
                style={{
                  color: '#1d476a',
                  backgroundColor: 'rgba(29, 71, 106, 0.05)'
                }}
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43V7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.43z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </nav>
  );
};

export default Navigation;